import 'bootstrap/dist/css/bootstrap.min.css';

import { useState } from 'react';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import { Grader } from 'src/scorer-signup/api/domains/grader';

import Card from '../literably-ui/components/card';
import LiterablyApi from './api/literably';
import styles from './app.css';

export type Props = {};

export default function App(_: Props) {
  const [proficientInSpanish, setProficientInSpanish] = useState(false);
  const [emailInvalid, setEmailInvalid] = useState(false);
  const [usernameInvalid, setUsernameInvalid] = useState(false);

  const handleSubmit = async (event: any): Promise<void> => {
    event.preventDefault();
    setEmailInvalid(false);
    setUsernameInvalid(false);

    const graderSignUpFormData = new FormData(event.target as HTMLFormElement);
    const graderSignUpBody: { [key: string]: any } = {};

    graderSignUpFormData.forEach((value, key) => {
      graderSignUpBody[key] = value;
    });
    // Override the value that FormData gives us ("on")
    graderSignUpBody.isSpanishProficient = proficientInSpanish;

    const literablyApi = new LiterablyApi();
    try {
      const createdGraderData: any = await literablyApi.createGrader(graderSignUpBody as Grader);
      if ('errors' in createdGraderData) {
        if ('email' in createdGraderData.errors) {
          return setEmailInvalid(true);
        }
        if ('username' in createdGraderData.errors) {
          return setUsernameInvalid(true);
        }
      }
      await literablyApi.loginGrader({ password: graderSignUpBody.password, username: graderSignUpBody.username });
      location.href = '/instructions/grader-directions';
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);
    }
  };

  const handleIsSpanishProficientChange = (event: any): void => {
    const isSpanishProficient = event.target.checked;
    setProficientInSpanish(isSpanishProficient);
  };

  return (
    <Container className={styles.signupForm}>
      <Row>
        <Col>
          <h1 className="mb-4">Scorer Sign Up</h1>
        </Col>
      </Row>
      <Row>
        <Col>
          <div className="alert alert-info text-center mb-4">
            Before you create an account, please look through the directions on{' '}
            <a href="/instructions/grader-directions">this page</a>. Please email{' '}
            <a href="mailto:grading@literably.com">grading@literably.com</a> if you have questions.{' '}
            <b>Please do not call or request a demo</b>.
          </div>
        </Col>
      </Row>
      <Row>
        <Col>
          <p>
            <b>Eligibility:</b> You must be at least 18 years old and neither live in, nor score from, the states of CA,
            CT, MA, MT, NE, or NJ.*
          </p>
        </Col>
      </Row>
      <Row>
        <Col>
          <p>
            <b>User Info:</b> If we approve you, we'll pay via Paypal using the credentials below. If you already have
            Paypal, be sure to use the email, first name, and last name of your Paypal account.
          </p>
        </Col>
      </Row>
      <Row className={styles.inputFields}>
        <Card
          sx={{
            border: '1px solid var(--table-border)',
            borderRadius: '8px',
            boxShadow: 'var(--box-shadow)',
          }}
        >
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3" controlId="formFirstName">
              <Form.Label>First Name</Form.Label>
              <Form.Control required type="text" name="firstName" placeholder="Enter first name" />
            </Form.Group>
            <Form.Group className="mb-3" controlId="formLastName">
              <Form.Label>Last Name</Form.Label>
              <Form.Control required type="text" name="lastName" placeholder="Enter last name" />
            </Form.Group>
            <Form.Group className="mb-3" controlId="formUsername">
              <Form.Label>Username</Form.Label>
              <Form.Control
                required
                type="text"
                name="username"
                placeholder="Enter username"
                isInvalid={usernameInvalid}
              />
              {usernameInvalid && (
                <Form.Text className={styles.errorText}>An account with this username already exists</Form.Text>
              )}
            </Form.Group>
            <Form.Group className="mb-3" controlId="formPassword">
              <Form.Label>Password</Form.Label>
              <Form.Control required type="password" name="password" placeholder="Password" />
            </Form.Group>
            <Form.Group className="mb-3" controlId="formEmail">
              <Form.Label>Email address</Form.Label>
              <Form.Control required type="email" name="email" placeholder="Enter email" isInvalid={emailInvalid} />
              {emailInvalid && (
                <Form.Text className={styles.errorText}>An account with this email already exists</Form.Text>
              )}
            </Form.Group>
            <Form.Group key="default-checkbox" className="mb-3">
              <Form.Check
                name="isSpanishProficient"
                type="checkbox"
                id="spanish-proficient"
                label="I am proficient in Spanish"
                onChange={handleIsSpanishProficientChange}
              />
            </Form.Group>
            {proficientInSpanish && (
              <Form.Group className="mb-3" controlId="formSpanishProficiency">
                <Form.Label>
                  Select your Spanish proficiency level based on our{' '}
                  <a href="https://literably-files.s3.amazonaws.com/spanish_proficiency_levels.pdf" target="_blank">
                    scale shown here
                  </a>
                </Form.Label>
                <Form.Select name="spanishProficiencyLevel" aria-label="Select your Spanish proficiency level.">
                  <option value="0">0</option>
                  <option value="1">1</option>
                  <option value="2">2</option>
                  <option value="3">3</option>
                  <option value="4">4</option>
                  <option value="5">5</option>
                </Form.Select>
              </Form.Group>
            )}
            <div className={styles.submitButton}>
              <Button variant="primary" type="submit">
                Submit
              </Button>
            </div>
          </Form>
        </Card>
      </Row>
      <Row className={styles.loginRow}>
        <Col>
          <p>
            <b>Already have an account?</b> <a href="/login">Log in</a>
          </p>
        </Col>
      </Row>
      <Row>
        <Col>
          <p>
            *As of November 15, 2019, we will no longer engage individuals located in the following U.S. states as
            independent contractors: California, Connecticut, Massachusetts, Montana, Nebraska, or New Jersey. By
            signing up, you are confirming that you neither live nor will be performing services for Literably in any of
            the listed states. Your representations to this effect may be subject to ongoing confirmation by Literably.
          </p>
        </Col>
      </Row>
    </Container>
  );
}
